<template>
  <div class="claim-edit">
    <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
      <h4 class="d-flex align-items-center">
        <font-awesome-icon icon="fas fa-laptop-house" />
        <span class="ml-1">報銷資料</span>
      </h4>
      <b-overlay
        :show="loading"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="sm"
      >
        <b-form class="mt-3" @submit.prevent="handleSubmit(onSubmit)" key="2">
          <div class="mt-2 mt-sm-3 position-relative">
            <b-row>
              <!-- Claim id -->
              <b-col cols="12" md="12">
                <b-form-group
                  label="報銷編號："
                  label-for="id"
                  label-cols-md="3"
                >
                  <div class="">
                    {{
                      newClaimData.id
                        ? newClaimData.claim_number
                        : "系統自動生成"
                    }}
                  </div>
                </b-form-group>
              </b-col>

              <!-- claim_date -->
              <b-col cols="12" md="12">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_submission_date"
                >
                  <b-form-group
                    label="報銷日期："
                    label-for="submission_date"
                    label-cols-md="3"
                    class="required"
                  >
                    <flat-pickr
                      v-model="newClaimData.submission_date"
                      class="form-control"
                      :config="{
                        allowInput: true,
                        altInput: true,
                        altFormat: 'Y-m-d',
                        dateFormat: 'Y-m-d',
                      }"
                      placeholder="輸入YYYY-MM-DD"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12">
                <hr />
              </b-col>

              <!-- user_id -->
              <b-col cols="12" md="12">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_user_id"
                >
                  <b-form-group
                    label="報銷者："
                    label-for="user_id"
                    label-cols-md="3"
                    class="required"
                  >
                    <v-select
                      id="user_id"
                      v-model="newClaimData.user_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="claimantUserOptions"
                      :clearable="!isWorkerPageWithUserRestriction"
                      label="name_cn"
                      :reduce="(option) => option.id"
                      placeholder="選擇報銷者"
                      :filter="userSearch"
                      :disabled="isWorkerPageWithUserRestriction"
                    >
                      <template #option="{ roles, name_cn }">
                        <template v-if="roles && roles[0]">
                          [{{ roles[0].name }}] {{ name_cn }}
                        </template>
                        <template v-else>
                          {{ name_cn }}
                        </template>
                      </template>
                      <template #selected-option="{ roles, name_cn }">
                        <template v-if="roles && roles[0]">
                          [{{ roles[0].name }}] {{ name_cn }}
                        </template>
                        <template v-else>
                          {{ name_cn }}
                        </template>
                      </template>
                    </v-select>
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- 需要發票選項 -->
              <b-col cols="12" md="12">
                <div
                  class="d-flex flex-wrap flex-md-nowrap align-items-center full_input"
                >
                  <label>需要發票？</label>
                  <div class="input_wrap">
                    <b-form-checkbox
                      id="requires_invoice"
                      v-model="newClaimData.requires_invoice"
                      name="requires_invoice"
                      switch
                      inline
                      :value="1"
                      :unchecked-value="0"
                      @change="handleRequiresInvoiceChange"
                    >
                    </b-form-checkbox>
                    {{
                      newClaimData.requires_invoice === 1
                        ? "需要發票"
                        : "不需要發票"
                    }}
                  </div>
                </div>
              </b-col>

              <!-- 發票選擇 -->
              <b-col cols="12" md="12" v-if="newClaimData.requires_invoice">
                <validation-provider
                  #default="validationContext"
                  name="報銷發票"
                  rules="required"
                  vid="v_invoice_id"
                >
                  <b-form-group
                    label="報銷發票："
                    label-for="invoice_id"
                    label-cols-md="3"
                    class="required"
                  >
                    <v-select
                      id="invoice_id"
                      v-model="newClaimData.invoice_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="invoiceOptions"
                      :clearable="true"
                      label="invoice_number"
                      :reduce="(option) => option.id"
                      placeholder="選擇報銷發票"
                      :filter="invoiceSearch"
                      :state="getValidationState(validationContext)"
                    >
                      <template #option="{ invoice_number, name_cn, address }">
                        [{{ invoice_number }}] {{ name_cn || "未指定客戶" }} -
                        {{ address || "未指定地址" }}
                      </template>
                      <template
                        #selected-option="{ invoice_number, name_cn, address }"
                      >
                        [{{ invoice_number }}] {{ name_cn || "未指定客戶" }} -
                        {{ address || "未指定地址" }}
                      </template>
                    </v-select>
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12">
                <hr />
              </b-col>

              <b-col cols="12" md="12">
                <b-form-group
                  label="報銷原因："
                  label-for="purpose"
                  label-cols-md="3"
                  class=""
                >
                  <b-form-textarea
                    id="purpose"
                    v-model="newClaimData.purpose"
                    placeholder="輸入報銷原因（可選）"
                    rows="3"
                  />
                </b-form-group>
              </b-col>


              <b-col cols="12">
                <hr />
              </b-col>

              <b-col cols="12">
                <!-- 工資 -->
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <h5 class="mb-0">報銷商品摘要</h5>
                  <b-button variant="success" size="sm" @click="addItem()">
                    <feather-icon size="16" icon="PlusIcon" />
                  </b-button>
                </div>

                <hr />
                <div>
                  <b-row
                    v-for="(expenseItem, index) in newClaimData.expense_items"
                    :key="`expense_item_${index}`"
                    class="px-1"
                  >
                    <b-col
                      cols="12"
                      class="d-flex backgroundColor3 mb-1 radius-round border-1"
                    >
                      <b-row class="flex-grow-1 p-2">
                        <!-- product_category_id -->
                        <b-col cols="12" md="6">
                          <b-row>
                            <b-col cols="12">
                              <validation-provider
                                #default="validationContext"
                                :vid="`v-product_category_id${index}`"
                                name="此欄"
                                rules="required"
                              >
                                <b-form-group
                                  :label="`代購分類：`"
                                  :label-for="`purchase_category_id${index}`"
                                  label-cols-md="2"
                                  class="required"
                                >
                                  <v-select
                                    v-model="expenseItem.purchase_category_id"
                                    :dir="
                                      $store.state.appConfig.isRTL
                                        ? 'rtl'
                                        : 'ltr'
                                    "
                                    :options="purchaseCategoryOptions"
                                    :clearable="false"
                                    label="label"
                                    :reduce="(option) => option.id"
                                    placeholder="選擇代購分類"
                                    :filter="purchaseCategorySearch"
                                  >
                                  </v-select>
                                  <b-form-invalid-feedback
                                    :state="
                                      getValidationState(validationContext)
                                    "
                                  >
                                    {{ validationContext.errors[0] }}
                                  </b-form-invalid-feedback>
                                </b-form-group>
                              </validation-provider>
                            </b-col>

                            <b-col cols="12">
                              <validation-provider
                                #default="validationContext"
                                :vid="`v-total${index}`"
                                name="此欄"
                                rules="required"
                              >
                                <b-form-group
                                  :label="`金額：`"
                                  :label-for="`total${index}`"
                                  label-cols-md="2"
                                  class="required"
                                >
                                  <b-input-group size="md" prepend="$">
                                    <b-form-input
                                      :id="`total${index}`"
                                      v-model="expenseItem.total"
                                      type="number"
                                      step="0.01"
                                      placeholder="輸入金額"
                                      :state="
                                        getValidationState(validationContext)
                                      "
                                      @input="calTotalAmount"
                                    />
                                  </b-input-group>
                                  <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                  </b-form-invalid-feedback>
                                </b-form-group>
                              </validation-provider>
                            </b-col>
                          </b-row>
                        </b-col>
                        <b-col cols="12" md="6">
                          <b-row>
                            <b-col cols="12">
                              <validation-provider
                                #default="validationContext"
                                :vid="`v-remark${index}`"
                                name="此欄"
                                rules="required"
                              >
                                <b-form-group
                                  :label="`代購內容：`"
                                  :label-for="`remark${index}`"
                                  label-cols-md="2"
                                  class="required"
                                >
                                  <b-form-textarea
                                    id="remark"
                                    v-model="expenseItem.remark"
                                    placeholder="代購內容"
                                    :state="
                                      getValidationState(validationContext)
                                    "
                                    rows="3"
                                  />
                                  <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                  </b-form-invalid-feedback>
                                </b-form-group>
                              </validation-provider>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>
                      <div
                        class="d-flex flex-column justify-content-between py-50 px-25"
                      >
                        <feather-icon
                          size="16"
                          icon="PlusIcon"
                          class="cursor-pointer text-success"
                          @click="addItem(index)"
                        />
                        <feather-icon
                          size="16"
                          icon="XIcon"
                          class="cursor-pointer text-danger"
                          @click="removeItem(index, expenseItem.id)"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </div>
              </b-col>

              <b-col
                cols="12"
                md="12"
                v-if="
                  (ability.can('status', 'claims_form') && !is_user_page) ||
                  (ability.can('status', 'user_claims_form') && is_user_page)
                "
              >
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_claim_status_id"
                >
                  <b-form-group
                    label="報銷狀態："
                    label-for="claim_status_id"
                    label-cols-md="3"
                  >
                    <v-select
                      id="claim_status_id"
                      v-model="newClaimData.claim_status_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="claimStatusOptions"
                      label="name"
                      :reduce="(option) => option.id"
                      placeholder="選擇報銷狀態"
                      :filter="claimStatusSearch"
                    >
                    </v-select>
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- 付款方式 -->
              <b-col cols="12"   v-if="
                  (ability.can('status', 'claims_form') && !is_user_page) ||
                  (ability.can('status', 'user_claims_form') && is_user_page)
                ">
                <b-form-group
                  :label="`付款方式：`"
                  :label-for="`payment_method_id`"
                  label-cols-md="3"
                >
                  <v-select
                    :id="`payment_method_id`"
                    v-model="newClaimData.payment_method_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="paymentMethodOptions"
                    :clearable="true"
                    label="name"
                    :reduce="(option) => option.id"
                    placeholder="選擇付款方式"
                    :filter="paymentMethodSearch"
                  >
                  </v-select>
                </b-form-group>
              </b-col>

              <!-- 支票號碼 - 當付款方式為支票時必填，其他情況可選填 -->
              <b-col   v-if="
                  (ability.can('status', 'claims_form') && !is_user_page) ||
                  (ability.can('status', 'user_claims_form') && is_user_page)
                ">
                <validation-provider
                  #default="validationContext"
                  :vid="`v-check_number`"
                  name="支票號碼"
                  :rules="newClaimData.payment_method_id == 1 ? 'required' : ''"
                >
                  <b-form-group
                    :label="`支票號碼：`"
                    :label-for="`check_number`"
                    label-cols-md="3"
                    :class="newClaimData.payment_method_id == 1 ? 'required' : ''"
                  >
                    <b-form-input
                      :id="`check_number`"
                      v-model="newClaimData.check_number"
                      placeholder="輸入支票號碼"
                      :readonly="newClaimData.payment_method_id != 1"
                      :disabled="newClaimData.payment_method_id != 1"
                      :state="getValidationState(validationContext)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="12">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_total_amount"
                >
                  <b-form-group
                    label="報銷金額："
                    label-for="total"
                    label-cols-md="3"
                    class="required"
                  >
                    <b-form-input
                      id="total"
                      v-model="newClaimData.total_amount"
                      placeholder="輸入報銷金額"
                      :disabled="true"
                      :state="getValidationState(validationContext)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <hr />

            <upload-file
              key="fileupload1"
              ref="childRef1"
              title="上傳單據"
              custome_id="1"
              :props_previewImage="newClaimData.documents"
              file_delete_url="/claims/documents"
              v-on:file-uploaded="onFileUploaded"
            ></upload-file>

            <hr />

            <div
              class="d-flex mt-1 flex-wrap flex-lg-nowrap justify-content-end"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-sm-2 mobile-w100"
                type="submit"
              >
                <feather-icon size="16" class="mr-1" icon="CheckCircleIcon" />
                提交
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
                class="mt-1 mt-sm-0 mobile-w100"
              >
                取消
              </b-button>
            </div>
          </div>
        </b-form>
      </b-overlay>
    </validation-observer>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormCheckbox,
  BFormRadioGroup,
  BFormRadio,
  BInputGroup,
  BButton,
  BOverlay,
  BFormTextarea,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import { ref, watch, computed } from "@vue/composition-api";
import vSelect from "vue-select";
import store from "@/store";
import Ripple from "vue-ripple-directive";
import "animate.css";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import zh_CN from "vee-validate/dist/locale/zh_CN.json";
localize("zh_cn", zh_CN);
import formValidation from "@core/comp-functions/forms/form-validation";
import { required } from "@validations";
import uploadFile from "@/layouts/components/file/uploadFile.vue";
import ability from "@/libs/acl/ability";

export default {
  data() {
    return {
      required,
      ability,
    };
  },
  directives: {
    Ripple,
  },
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormInvalidFeedback,
    BFormCheckbox,
    BFormRadioGroup,
    BFormRadio,
    BInputGroup,
    BRow,
    BCol,
    BButton,
    BOverlay,
    flatPickr,
    vSelect,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    uploadFile,
  },
  computed: {
    // 检查是否为worker页面且有user_id限制
    isWorkerPageWithUserRestriction() {
      return this.is_user_page && this.$route.query.user_id;
    },
    // 获取受限制的用户ID
    restrictedUserId() {
      if (this.isWorkerPageWithUserRestriction) {
        return Number(this.$route.query.user_id);
      }
      return null;
    },
    // 报销者选项（在worker页面时只显示指定用户）
    claimantUserOptions() {
      if (this.isWorkerPageWithUserRestriction) {
        const userId = this.restrictedUserId;
        return this.userOptions.filter(user => user.id === userId);
      }
      return this.userOptions;
    },
  },
  methods: {
    onFileUploaded(file) {
      this.newClaimData.image_files = file;
    },

    hide() {
      if (this.$route.name.indexOf("worker-claim") > -1) {
        this.$router.go(-1);
      } else {
        this.$router.replace("/claim/list");
      }
    },

    onSubmit() {
      this.$swal({
        title: "你確定嗎?",
        showCancelButton: true,
        confirmButtonText: "提交",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.loading = true;

          let formData = new FormData();

          formData.append("id", this.newClaimData.id);
          formData.append("user_id", this.newClaimData.user_id);
          formData.append("requires_invoice", this.newClaimData.requires_invoice || 0);
          if (this.newClaimData.invoice_id) {
            formData.append("invoice_id", this.newClaimData.invoice_id);
          }
          formData.append("submission_date", this.newClaimData.submission_date);
          formData.append("purpose", this.newClaimData.purpose || ""); // 確保 purpose 不會是 undefined

          // 新增付款方式和支票號碼欄位（即使為空也要傳遞，以便清空欄位）
          formData.append("payment_method_id", this.newClaimData.payment_method_id || "");
          formData.append("check_number", this.newClaimData.check_number || "");
          if (
            (ability.can("status", "claims_form") && !this.is_user_page) ||
            (ability.can("status", "user_claims_form") && this.is_user_page)
          ) {
            formData.append(
              "claim_status_id",
              this.newClaimData.claim_status_id
            );
          }
          for (let i = 0; i < this.newClaimData.expense_items.length; i++) {
            for (let key of Object.keys(this.newClaimData.expense_items[i])) {
              formData.append(
                `expense_items[${i}][${key}]`,
                this.newClaimData.expense_items[i][key]
              );
            }
          }

          formData.append("total_amount", this.newClaimData.total_amount);

          if (
            this.newClaimData.image_files &&
            this.newClaimData.image_files.length > 0
          ) {
            this.newClaimData.image_files.forEach((item) => {
              formData.append("documents[]", item);
            });
          }
          store
            .dispatch("claims/editClaim", formData)
            .then((response) => {
              this.loading = false;
              this.$swal({
                text: response.data.message,
                icon: "success",
                confirmButtonText: "確定",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.hide();
              this.$emit("refetech-data");
            })
            .catch((error) => {
              this.loading = false;
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                confirmButtonText: "確定",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    deleteClaimDocument(id) {
      this.$swal({
        title: "你確定嗎?",
        text: "您的決定是最終決定，您將無法取回這記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          store
            .dispatch("claims/deleteClaimDocument", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.$emit("refetch-data");
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    makeToast(variant = null, title = null, content = null) {
      this.$bvToast.toast(content, {
        title: title,
        variant,
        solid: true,
      });
    },
    userSearch(options, search) {
      return options.filter((item) => {
        return item.name_cn.toLowerCase().includes(search.toLowerCase()) ||
          item.roles[0].name.toLowerCase().includes(search.toLowerCase());
      });
    },
    invoiceSearch(options, search) {
      return options.filter((item) => {
        return (
          item.invoice_number.toLowerCase().includes(search.toLowerCase()) ||
            item.name_cn.toLowerCase().includes(search.toLowerCase()) ||
            item.address.toLowerCase().includes(search.toLowerCase())
        );
      });
    },
    purchaseCategorySearch(options, search) {
      return options.filter((item) => {
        return item.label.toLowerCase().includes(search.toLowerCase());
      });
    },
    claimStatusSearch(options, search) {
      return options.filter((item) => {
        return item.name.toLowerCase().includes(search.toLowerCase());
      });
    },
    paymentMethodSearch(options, search) {
      return options.filter((item) => {
        return item.name.toLowerCase().includes(search.toLowerCase());
      });
    },
    handleRequiresInvoiceChange(value) {
      // 當切換發票需求時，清空發票選擇
      if (!value) {
        this.newClaimData.invoice_id = null;
      }
    },

    addItem(index) {
      if (!this.newClaimData.expense_items) {
        this.newClaimData.expense_items = [];
      }

      const newItem = JSON.parse(
        JSON.stringify({
          id: 0,
          purchase_category_id: null,
          total: 0,
          remark: "",
        })
      );

      if (index !== undefined) {
        this.newClaimData.expense_items.splice(index + 1, 0, newItem);
      } else {
        this.newClaimData.expense_items.push(newItem);
      }
    },
    removeItem(index) {
      this.newClaimData.expense_items.splice(index, 1);
      this.calTotalAmount();
    },
  },
  props: {
    claimsData: {
      type: Object,
      required: true,
    },
    workerOptions: {
      type: Array,
      required: true,
    },
    userOptions: {
      type: Array,
      required: true,
    },
    approvalStatusOptions: {
      type: Array,
      required: true,
    },
    claimStatusOptions: {
      type: Array,
      required: true,
    },
    invoiceOptions: {
      type: Array,
      required: true,
    },
    paymentMethodOptions: {
      type: Array,
      required: true,
    },
    role: {},
    is_user_page: {
      type: Boolean,
      required: true,
    },
  },

  setup(props, { emit }) {
    const newClaimData = ref({
      id: 0,
      purpose: '', // 初始化 purpose 為空字串
      requires_invoice: 0 // 初始化需要發票為 0（不需要）
    });

    // 代購分類選項
    const purchaseCategoryOptions = ref([
      { id: 1, label: "工具/五金用品" },
      { id: 2, label: "運輸(叫車)" },
    ]);

    // 付款方式選項



    watch(
      () => props.claimsData,
      (newVal) => {
        newClaimData.value = newVal;
        newClaimData.value.total_amount = newClaimData.value.total_amount
          ? Math.round(Number(newClaimData.value.total_amount) * 10) / 10
          : 0;
      }
    );

    // 監聽路由變化，在worker頁面自動填入用戶
    watch(
      () => [props.is_user_page, props.userOptions],
      ([isUserPage, userOptions]) => {
        if (isUserPage && userOptions && userOptions.length > 0) {
          // 檢查URL是否包含user_id參數
          const urlParams = new URLSearchParams(window.location.search);
          const userId = urlParams.get('user_id');
          
          if (userId && newClaimData.value.id === 0) {
            const userIdNum = Number(userId);
            
            // 設置報銷者
            if (!newClaimData.value.user_id) {
              newClaimData.value.user_id = userIdNum;
            }
          }
        }
      },
      { immediate: true, deep: true }
    );

    // refetchProjectUsers(newClaimData.value);
    const resetClaimsData = () => {
      newClaimData.value = JSON.parse(
        JSON.stringify({
          id: 0,
          purpose: '', // 重置時也要初始化 purpose 為空字串
          requires_invoice: 0 // 重置時也要初始化需要發票為 0（不需要）
        })
      );
    };

    const loading = ref(false);

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetClaimsData);

    const calTotalAmount = () => {
      let amount = 0;

      if (
        newClaimData.value.expense_items &&
        newClaimData.value.expense_items.length > 0
      ) {
        amount += newClaimData.value.expense_items.reduce(
          (a, b) => a + (Number(b.total) ? Number(b.total) : 0),
          0
        );
      }
      newClaimData.value.total_amount = Math.round(Number(amount) * 10) / 10;
    };

    return {
      loading,
      refFormObserver,
      getValidationState,
      resetForm,
      newClaimData,
      purchaseCategoryOptions,
      calTotalAmount,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";
.claim-edit {
  .full_input label {
    width: calc(30% - 10px);
  }
}
</style>

<style lang="scss" scoped>
</style>
